/*
 * =====================================================================================================
 * 📁 api.ts
 * @description
 *    Crea una instancia personalizada de Axios con:
 *      🔹 BaseURL configurable
 *      🔹 Timeout por defecto
 *      🔹 Interceptor global de respuestas que detecta errores de red
 *      🔹 Integración con `retryManager` para mostrar modal de reintento si no hay conexión
 * =====================================================================================================
 */

import axios from "axios";
import { retryManager } from "@/composables/useRetryManager";
import { checkConnection } from "./checkConnection";

let lastCheck = 0;
const DEFAULT_TIMEOUT = 30000;

/**
 * 🚀 createApiInstance
 *
 * Crea una instancia de Axios que añade lógica global de gestión de errores de red.
 * Si una petición falla por red (no por error HTTP), se verifica si la conexión está realmente caída
 * y en ese caso se guarda la acción para reintento posterior desde un modal.
 *
 * @param baseURL - URL base para la instancia Axios
 * @param timeout - Tiempo máximo de espera para cada petición (por defecto 10s)
 * @returns instancia de Axios lista para usarse
 */
export function createApiInstance(baseURL: string, timeout = DEFAULT_TIMEOUT) {
  const api = axios.create({
    baseURL,
    timeout,
  });

  console.log("⏱️ Axios timeout configurado:", api.defaults.timeout);

  // 🌐 Interceptor de respuesta para capturar errores de red
  api.interceptors.response.use(
    (res) => res, // ✅ Si la respuesta es válida, la devolvemos sin tocarla
    async (error) => {
      // ⚠️ Detecta errores de red: sin respuesta, timeout, conexión abortada...
      const isNetworkError =
        !error.response ||
        error.code === "ECONNABORTED" ||
        error.message.includes("timeout");

      const THROTTLE_MS = 3000; // mínimo 3s entre checks

      if (isNetworkError) {
        const now = Date.now();
        if (now - lastCheck > THROTTLE_MS) {
          lastCheck = now;
          // 🔍 Confirmamos si realmente no hay conexión (ping real)
          const isConnected = await checkConnection();

          if (!isConnected) {
            // 💾 Guardamos la acción fallida para permitir reintento más tarde
            console.warn("🌐 Red caída. Guardando acción pendiente...");
            retryManager.setPendingAction(() => api.request(error.config));
          }
        }
      }

      // ❌ Propagamos siempre el error (aunque haya retryManager)
      return Promise.reject(error);
    },
  );

  return api;
}
